import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifyInfo, notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { ActionIcon, Badge, Box, Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Popover } from '@repletoryreach/sdk-ui/overlays';
import { useTemplateEvent } from '../../data/useTemplateEvent';
import { TenantName } from '../../components/TenantName';
import { formatRelative } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';

type Kind = 'submitted' | 'approved' | 'rejected';

interface BellItem {
  id: string;
  kind: Kind;
  code: string;
  organisationId: string;
  reason?: string | null;
  ts: string;
  read: boolean;
}

const KIND_KEY: Record<Kind, string> = {
  submitted: 'notifications.template.submitted',
  approved: 'notifications.template.approved',
  rejected: 'notifications.template.rejected'
};

const MAX_ITEMS = 20;

/**
 * Template-lifecycle events from the SignalR fanout, as a panel rather than a
 * counter. Clicking the bell used to reset the number to zero and open nothing,
 * so an event that arrived while the operator was reading a table left no trace
 * at all. The events are kept here, newest first, and "mark all as read" is the
 * thing that clears the badge.
 *
 * The organisation is named, never numbered: the toast bodies used to lead with
 * eight characters of the org GUID. The name comes from the tenants list in the
 * query cache, read without subscribing so the bell never triggers a fetch of
 * its own; the panel resolves the rest through `<TenantName>` when it is open.
 */
export function NotificationsBell() {
  const t = useT();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<BellItem[]>([]);
  const [opened, setOpened] = useState(false);

  const unread = items.filter((i) => !i.read).length;

  /** The organisation's name if the tenants list is already cached, else a neutral phrase. */
  const tenantLabel = useCallback((organisationId: string): string => {
    const cached = queryClient.getQueryData<{ data: Array<{ id: string; name: string }> }>(['admin', 'tenants']);
    return cached?.data.find((row) => row.id === organisationId)?.name ?? t('notifications.unknownTenant');
  }, [queryClient, t]);

  const push = useCallback((item: Omit<BellItem, 'ts' | 'read'>) => {
    setItems((prev) => [{ ...item, ts: new Date().toISOString(), read: false }, ...prev].slice(0, MAX_ITEMS));
  }, []);

  useTemplateEvent('template.submitted', (payload) => {
    push({ id: `${payload.template_id}-sub-${Date.now()}`, kind: 'submitted', code: payload.code, organisationId: payload.organisation_id });
    notifyInfo(
      t('notifications.toast.submitted', { code: payload.code, tenant: tenantLabel(payload.organisation_id) }),
      t('notifications.template.submitted')
    );
  });
  useTemplateEvent('template.approved', (payload) => {
    push({ id: `${payload.template_id}-app-${Date.now()}`, kind: 'approved', code: payload.code, organisationId: payload.organisation_id });
    notifySuccess(
      t('notifications.toast.approved', { code: payload.code, tenant: tenantLabel(payload.organisation_id) }),
      t('notifications.template.approved')
    );
  });
  useTemplateEvent('template.rejected', (payload) => {
    push({
      id: `${payload.template_id}-rej-${Date.now()}`,
      kind: 'rejected',
      code: payload.code,
      organisationId: payload.organisation_id,
      reason: payload.reason
    });
    notifyError(
      t('notifications.toast.rejected', {
        code: payload.code,
        tenant: tenantLabel(payload.organisation_id),
        reason: payload.reason ?? t('notifications.template.noReason')
      }),
      t('notifications.template.rejected')
    );
  });

  return (
    <Box style={{ position: 'relative' }}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-end"
        width={340}
        shadow="lg"
      >
        <Popover.Target>
          <ActionIcon variant="subtle" aria-label={t('notifications.bellAria')}>
            <BellIcon />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          {/* The dropdown brings its own md padding. */}
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text role="bodySmall" fw={700}>{t('notifications.title')}</Text>
              {unread > 0 && (
                <Button
                  size="xs"
                  variant="subtle"
                  onClick={() => setItems((prev) => prev.map((i) => ({ ...i, read: true })))}
                >
                  {t('notifications.markAllRead')}
                </Button>
              )}
            </Group>

            {items.length === 0 ? (
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('notifications.empty')}</Text>
            ) : (
              <Stack gap="sm">
                {items.map((item) => (
                  <Stack key={item.id} gap="xs">
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <Text role="bodySmall" fw={item.read ? 400 : 700}>{t(KIND_KEY[item.kind])}</Text>
                      <Text role="caption" c="var(--rr-color-text-muted)">{formatRelative(item.ts)}</Text>
                    </Group>
                    <Group gap="xs" align="baseline" wrap="nowrap">
                      <Text role="caption"><code>{item.code}</code></Text>
                      <TenantName id={item.organisationId} subtle />
                    </Group>
                    {item.reason && (
                      <Text role="caption" c="var(--rr-color-text-muted)">{item.reason}</Text>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
        </Popover.Dropdown>
      </Popover>

      {unread > 0 && (
        <Badge
          size="xs"
          color="red"
          style={{ position: 'absolute', top: -4, right: -4, pointerEvents: 'none' }}
        >
          {unread > 99 ? '99+' : unread}
        </Badge>
      )}
    </Box>
  );
}

function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 6a5 5 0 1110 0c0 4 1.5 5 1.5 5h-13S3 10 3 6z" />
      <path d="M6.5 13a1.5 1.5 0 003 0" />
    </svg>
  );
}
